import React from 'react';
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../hooks/useCart';
import { useNavigate } from 'react-router-dom';

const CartDrawer = () => {
  const { items, removeItem, updateQuantity, totalItems, totalPrice, isCartOpen, setIsCartOpen } = useCart();
  const navigate = useNavigate();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col" data-testid="cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8DFD3]">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#5C7A3A]" />
            <h2 className="text-lg font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your Cart
            </h2>
            <span className="bg-[#5C7A3A] text-white text-xs font-bold px-2 py-0.5 rounded-full">{totalItems}</span>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            data-testid="close-cart-btn"
          >
            <X size={20} className="text-[#7a6555]" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="cart-empty">
              <ShoppingBag size={48} className="text-[#D4A574]/30 mb-4" />
              <p className="text-lg font-semibold text-[#2C1810] mb-1">Your cart is empty</p>
              <p className="text-sm text-[#7a6555] mb-4">Add some delicious items to get started</p>
              <Button
                onClick={() => { setIsCartOpen(false); navigate('/products'); }}
                className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer"
                data-testid="browse-products-btn"
              >
                Browse Products
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(item => (
                <div key={item.product_id} className="flex gap-3 p-3 bg-[#FDF8F0] rounded-xl" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#2C1810] truncate">{item.name}</p>
                    <p className="text-sm font-semibold text-[#5C7A3A]">{'\u20B9'}{item.price}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="w-7 h-7 rounded-lg bg-white border border-[#E8DFD3] flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors"
                        data-testid={`qty-minus-${item.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-bold text-[#2C1810] w-6 text-center" data-testid={`qty-value-${item.product_id}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 rounded-lg bg-white border border-[#E8DFD3] flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors"
                        data-testid={`qty-plus-${item.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                    <p className="text-sm font-bold text-[#2C1810]">{'\u20B9'}{(item.price * item.quantity).toFixed(0)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#E8DFD3] px-5 py-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[#7a6555]">Subtotal ({totalItems} items)</span>
              <span className="text-xl font-bold text-[#2C1810]">{'\u20B9'}{totalPrice.toFixed(0)}</span>
            </div>
            <Button
              onClick={() => { setIsCartOpen(false); navigate('/checkout'); }}
              className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-5 cursor-pointer"
              data-testid="proceed-checkout-btn"
            >
              Proceed to Checkout <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
